import React, { useEffect, useRef, useState } from "react";
import Tabs from "../../components/tab";
import Buyer from "./buyer";
import Merchant from "./merchant";
import useRequest from "../../components/hooks/use-request";
import Icon from "../../assets/icons";
import Pagination from "../../components/pagination/pagination";
import NotificationModal from "../notification/notification-modal";
import dayjs from "dayjs";

interface UserData {
  fullname: string;
}

const Dashboard = () => {
  const [data, setData] = useState<UserData[]>([]);
  const [statistics, setStatistics] = useState([]);
  const [activeTab, setActiveTab] = useState<"merchant" | "buyer">("merchant");
  const [searchQuery, setSearchQuery] = useState("");
  const userToken = localStorage.getItem("token");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const { makeRequest } = useRequest("/users", "GET", {
    Authorization: `Bearer ${userToken}`,
  });
  const params = new URLSearchParams(new URL(window.location.href).search);
  const [currentPage, setCurrentPage] = useState(
    Number(params.get("page") || 1)
  );
  const [totalPages, setTotalPages] = useState<number>(1);
  const itemsPerPage = 10;
  const { makeRequest: getStat } = useRequest("/users/statistics", "GET", {
    Authorization: `Bearer ${userToken}`,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(params.get("search") || "");
  // Tracks the most recent fetch so late-arriving (superseded) responses are ignored
  const latestRequestId = useRef(0);

  // Debounce the search input by 1000ms before it triggers a fetch
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 1000);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const openNotification = () => {
    setModalVisible(true);
  };

  const updateUrlParams = (params: {
    [key: string]: string | number | null;
  }) => {
    const url = new URL(window.location.href);
    Object.keys(params).forEach((key) => {
      if (params[key] !== null) {
        url.searchParams.set(key, params[key]!.toString());
      } else {
        url.searchParams.delete(key);
      }
    });
    window.history.pushState({}, "", url.toString());
  };

  useEffect(() => {
    const storedSearchQuery = params.get("search") || "";
    const storedStatus = params.get("status") || "";
    const storedUserType = params.get("userType") || activeTab;
    const storedStartDate = params.get("startDate") || "";
    const storedEndDate = params.get("endDate") || "";

    setSearchQuery(storedSearchQuery);
    setSelectedStatus(storedStatus);
    setActiveTab(storedUserType as "merchant" | "buyer");
    setStartDate(storedStartDate);
    setEndDate(storedEndDate);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getStat();
      setStatistics(response.data);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateUrlParams({
      page: currentPage,
      search: debouncedSearch,
      status: selectedStatus,
      userType: activeTab,
      startDate,
      endDate,
    });
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, debouncedSearch, selectedStatus, currentPage, startDate, endDate]);

  const fetchData = async () => {
    const page = currentPage;
    const limit = itemsPerPage;
    const params: {
      search?: string;
      userType?: string;
      startDate?: string;
      endDate?: string;
      status?: string;
      limit: number;
      page: number;
    } = {
      limit,
      page,
    };

    if (activeTab === "merchant") {
      params.userType = "merchant";
    } else if (activeTab === "buyer") {
      params.userType = "buyer";
    }

    if (selectedStatus) {
      params.status = selectedStatus;
    }
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    if (startDate) {
      params.startDate = startDate;
    }
    if (endDate) {
      params.endDate = endDate;
    }

    const requestId = ++latestRequestId.current;
    setLoading(true);

    const [response] = await makeRequest(undefined, params);

    // A newer filter/page request was fired while this one was in flight —
    // discard this stale response so it can't overwrite the latest results.
    if (requestId !== latestRequestId.current) return;

    setData(response.data?.users || []);
    setTotalPages(Math.ceil(response.data?.totalPages));
    setLoading(false);
  };

  function handlePageChange(page: number) {
    setCurrentPage(page);
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(event.target.value);
  };

  // Reset clears both the status filter (back to "All") and the date range
  const handleResetFilters = () => {
    setSelectedStatus("");
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
  };


  const handleDateRangeChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null]
  ) => {
    if (dates) {
      let [start, end] = dates;
      if (start && end && start < end) {
        [start, end] = [end, start];
      }
      setStartDate(start ? start.format("YYYY-MM-DD") : null);
      setEndDate(end ? end.format("YYYY-MM-DD") : null);
    }
  };


  return (
    <>
      <div className="flex justify-between w-[95%]">
        <section>
          <h2 className="text-[24px] font-bold">
            Accounts{" "}
            <p className="text-[14px] font-normal">
              See all the accounts / profiles of users
            </p>
          </h2>
        </section>

        <section className="flex gap-4">
          <div className="border-2 rounded-md solid pl-5 bg-transparent h-[45px] flex gap-3">
            <Icon name="searchIcon" className="mt-3 rounded" />
            <input
              className="outline-none border-none w-[80%] bg-transparent"
              id="input-placeholder"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <Icon name="downloadIcon" />

          <button className="-mt-3" onClick={openNotification}>
            <Icon name="notificationIcon" />
          </button>
        </section>
      </div>
      <div className="flex justify-between">
        <Tabs
          activeTab={activeTab}
          tabs={["merchant", "buyer"]}
          setActiveTab={setActiveTab}
        />
      </div>
      <div>
        {activeTab === "merchant" && (
          <Merchant
            data={data}
            activeTab={activeTab}
            statistics={statistics}
            selectedStatus={selectedStatus}
            handleStatusChange={handleStatusChange}
            startDate={startDate}
            endDate={endDate}
            handleDateRangeChange={handleDateRangeChange}
            handleResetFilters={handleResetFilters}
            loading={loading}
          />
        )}
        {activeTab === "buyer" && (
          <Buyer
            data={data}
            activeTab={activeTab}
            statistics={statistics}
            selectedStatus={selectedStatus}
            handleStatusChange={handleStatusChange}
            startDate={startDate}
            endDate={endDate}
            handleDateRangeChange={handleDateRangeChange}
            handleResetFilters={handleResetFilters}
            loading={loading}
          />
        )}
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      <NotificationModal
        visible={modalVisible}
        handleClose={() => setModalVisible(false)}
      />
    </>
  );
};

export default Dashboard;
